import React, { createRef, useEffect, useState } from "react";
import {
  DrawingFileTypeModel,
  LocaleModel,
  drawingApi,
} from "../api/drawingApi";
import { Col, Form } from "react-bootstrap";
import { Problem } from "../api/backendApi";
import { CloudPlus } from "react-bootstrap-icons";

interface PDFLinkEditProps {
  vehicleFileNumber: string;
  onChange: (pdfId: string) => void;
  onError: (error: Problem | string | undefined) => void;
}

function PDFLinkEdit({
  vehicleFileNumber,
  onChange,
  onError,
}: PDFLinkEditProps) {
  const [pdfIds, setPdfIds] = useState<string[]>();
  const [selectedPdfId, setSelectedPdfId] = useState<string>();
  const refFileInput = createRef<HTMLInputElement>();

  useEffect(() => {
    async function getDrawingPdfs() {
      const result = await drawingApi.getDrawingFiles(
        vehicleFileNumber,
        DrawingFileTypeModel.PDF,
      );

      if (result.ok) {
        onError(undefined);
        let newPdfs = result.value.map((p) => p.name);

        setPdfIds(newPdfs);

        let newSelectedPdfId = "";

        if (selectedPdfId) {
          const selectedIndex = newPdfs.indexOf(selectedPdfId);

          if (selectedIndex > 0) newSelectedPdfId = selectedPdfId;
        }

        setSelectedPdfId(newSelectedPdfId);
        onChange(newSelectedPdfId);
      } else {
        onError(result.problem);
      }
    }

    getDrawingPdfs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleFileNumber]);

  const handleInputFileChanged = async (files: FileList | null) => {
    if (!files || files.length !== 1) return;

    const file = files[0];

    const result = await drawingApi.uploadDrawingFile(
      vehicleFileNumber,
      file,
      file.name,
      LocaleModel.English,
      DrawingFileTypeModel.PDF,
    );

    if (result.ok) {
      if (result.value !== file.name) {
        onError(`The file already exists with the name '${result.value}'.`);
        setSelectedPdfId(result.value);
        return;
      }
      onError(undefined);
      setSelectedPdfId(file.name);

      if (!pdfIds || pdfIds.indexOf(file.name) < 0) {
        let newPdfIds = [...(pdfIds ?? []), file.name];
        newPdfIds.sort();
        setPdfIds(newPdfIds);
        onChange(file.name);
      }
    } else {
      onError(result.problem);
    }
  };

  return (
    <>
      <Col>
        <Form.Select
          size="sm"
          value={selectedPdfId}
          disabled={!pdfIds}
          onChange={(c) => {
            setSelectedPdfId(c.target.value);
            onChange(c.target.value);
          }}
        >
          {!pdfIds && <option value={undefined}>loading...</option>}
          {pdfIds && pdfIds.length === 0 && (
            <option value="">no pdfs available...</option>
          )}
          {pdfIds && pdfIds.length > 0 && (
            <>
              <option key="" value="">
                [select]
              </option>
              {pdfIds.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </>
          )}
        </Form.Select>
      </Col>
      <Col md="auto">
        <CloudPlus
          className="mx-1"
          role="button"
          size="1.5em"
          title="Upload new PDF file."
          onClick={() => refFileInput.current?.click()}
        />
        <input
          type="file"
          ref={refFileInput}
          style={{ display: "none" }}
          onChange={(e) => handleInputFileChanged(e.target.files)}
          accept=".pdf"
        ></input>
      </Col>
    </>
  );
}

export default PDFLinkEdit;
